import React, { useState, useEffect, useRef } from 'react'
import '../styles/dashboard.css'
import '../styles/students.css'
import Sidebar from '../components/Sidebar'
import Header from '../components/Header'
import PrintReportButton from '../components/PrintReportButton'
import DateRangeFilter from '../components/DateRangeFilter'
import ViewRecordModal from '../components/ViewRecordModal'
import EditStudent from './EditStudent'
import ListPagination from '../components/ListPagination'
import useListPagination from '../hooks/useListPagination'
import { EMPTY_DATE_RANGE, hasDateRange, isDateInRange } from '../utils/dateRange'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faPlus, faEdit, faTrash, faSearch, faChevronDown, faTimes, faArrowLeft, faUserTie,
  faKey, faCheckCircle, faExclamationTriangle, faEye
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

export default function Students() {
    const [students, setStudents] = useState([]);
    const [leads, setLeads] = useState([]);
    const [assignmentTab, setAssignmentTab] = useState('students'); // 'students' | 'leads'
    const [viewRecordId, setViewRecordId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [columnFilters, setColumnFilters] = useState({
        fullName: [], country: []
    });
    const [filterSearch, setFilterSearch] = useState({
        fullName: '', country: ''
    });
    const [counselorInfo, setCounselorInfo] = useState(null);

    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [credentialsLoading, setCredentialsLoading] = useState(false);
    const [credentialsData, setCredentialsData] = useState(null);
    const [credentialsError, setCredentialsError] = useState('');

    const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const counselorId = searchParams.get('counselorId');

    useEffect(() => {
        if (counselorId) {
            fetchStudentsByCounselor(counselorId);
        } else {
            setLeads([]);
            setAssignmentTab('students');
            setCounselorInfo(null);
            fetchStudents();
        }
    }, [counselorId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.filter-dropdown-container')) setActiveFilter(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchStudents = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/students`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const data = await response.json();
            if (data.success) setStudents(data.students);
            else setError(data.message || 'Failed to fetch students');
        } catch (err) {
            setError('Network error. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentsByCounselor = async (cId) => {
        setLoading(true);
        setError('');
        try {
            const counselorRes = await fetch(`${API_URL}/counselors/${cId}`);
            const counselorData = await counselorRes.json();
            if (counselorData.success) {
                setCounselorInfo(counselorData.counselor);
                setLeads(counselorData.assignedLeads || []);
                const assignedStudents = counselorData.assignedStudents || [];
                const studentDetails = await Promise.all(
                    assignedStudents.map(async (s) => {
                        try {
                            const res = await fetch(`${API_URL}/students/${s.id}`);
                            const d = await res.json();
                            return d.success ? d.student : s;
                        } catch {
                            return s;
                        }
                    })
                );
                setStudents(studentDetails);
            } else {
                setError('Failed to load counselor data');
            }
        } catch (err) {
            setError('Network error. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (studentId, studentName) => {
        if (!window.confirm(`Are you sure you want to delete ${studentName}? This will delete all associated data.`)) return;
        try {
            const response = await fetch(`${API_URL}/students/${studentId}`, { method: 'DELETE' });
            const data = await response.json();
            if (data.success) {
                alert('Student deleted successfully');
                counselorId ? fetchStudentsByCounselor(counselorId) : fetchStudents();
            } else alert(data.message || 'Failed to delete student');
        } catch (err) {
            alert('Network error. Please try again.');
        }
    };

    const handleShowCredentials = async (studentId) => {
        setCredentialsLoading(true);
        setCredentialsError('');
        setCredentialsData(null);
        setShowCredentialsModal(true);
        try {
            const response = await fetch(`${API_URL}/students/${studentId}/credentials`);
            const data = await response.json();
            if (data.success) {
                setCredentialsData(data.credentials);
            } else {
                setCredentialsError(data.message || 'Failed to load credentials');
            }
        } catch (err) {
            setCredentialsError('Network error. Please try again.');
        } finally {
            setCredentialsLoading(false);
        }
    };

    const getFullName = (student) => {
        return [student.name, student.middle_name, student.surname].filter(Boolean).join(' ');
    };

    const getUniqueValues = (column) => {
        const values = new Set();
        students.forEach(s => {
            let value;
            switch (column) {
                case 'fullName': value = getFullName(s); break;
                case 'country': value = s.country; break;
                default: value = '';
            }
            if (value) values.add(value);
        });
        return Array.from(values).sort();
    };

    const getFilteredOptions = (column) => {
        const unique = getUniqueValues(column);
        const term = filterSearch[column].toLowerCase();
        return term ? unique.filter(v => v.toLowerCase().includes(term)) : unique;
    };

    const toggleFilterValue = (column, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: prev[column].includes(value)
                ? prev[column].filter(v => v !== value)
                : [...prev[column], value]
        }));
    };

    const clearColumnFilter = (column) => {
        setColumnFilters(prev => ({ ...prev, [column]: [] }));
        setFilterSearch(prev => ({ ...prev, [column]: '' }));
    };

    const clearAllFilters = () => {
        setColumnFilters({ fullName: [], country: [] });
        setFilterSearch({ fullName: '', country: '' });
        setSearchTerm('');
        setDateRange(EMPTY_DATE_RANGE);
    };

    const formatLeadCountries = (lead) => {
        if (!lead) return '-';
        let countries = [];
        if (lead.countries_of_interest) {
            try {
                const parsed = typeof lead.countries_of_interest === 'string'
                    ? JSON.parse(lead.countries_of_interest)
                    : lead.countries_of_interest;
                if (Array.isArray(parsed)) countries = parsed.map(c => String(c).trim()).filter(Boolean);
                else if (parsed) countries = [String(parsed)];
            } catch {
                countries = String(lead.countries_of_interest).split(',').map(c => c.trim()).filter(Boolean);
            }
        }
        if (lead.countries_other?.trim()) countries.push(lead.countries_other.trim());
        return countries.length ? countries.join(', ') : '-';
    };

    const filteredStudents = students.filter(s => {
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            const name = getFullName(s).toLowerCase();
            if (!name.includes(term) && !s.email?.toLowerCase().includes(term) &&
                !s.mobile?.includes(term) && !s.student_id?.toLowerCase().includes(term)) return false;
        }
        if (columnFilters.fullName.length > 0 && !columnFilters.fullName.includes(getFullName(s))) return false;
        if (columnFilters.country.length > 0 && !columnFilters.country.includes(s.country)) return false;
        if (hasDateRange(dateRange) && !isDateInRange(s.created_at, dateRange.from, dateRange.to)) return false;
        return true;
    });

    const filteredLeads = leads.filter(l => {
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            if (
                !l.full_name?.toLowerCase().includes(term) &&
                !l.email?.toLowerCase().includes(term) &&
                !l.phone?.includes(term) &&
                !String(l.id).includes(term)
            ) return false;
        }
        if (hasDateRange(dateRange) && !isDateInRange(l.created_at || l.assigned_at, dateRange.from, dateRange.to)) return false;
        return true;
    });

    const {
        paginatedItems: paginatedStudents,
        totalPages,
        currentPage: safeCurrentPage,
        goToPage,
    } = useListPagination(
        filteredStudents,
        [searchTerm, JSON.stringify(columnFilters), dateRange.from, dateRange.to, counselorId, assignmentTab].join('|')
    );

    const {
        paginatedItems: paginatedLeads,
        totalPages: leadTotalPages,
        currentPage: leadCurrentPage,
        goToPage: goToLeadPage,
    } = useListPagination(
        filteredLeads,
        [searchTerm, dateRange.from, dateRange.to, counselorId, assignmentTab].join('|')
    );

    const hasActiveFilters = Object.values(columnFilters).some(f => f.length > 0) || searchTerm.trim() || hasDateRange(dateRange);
    const badgeCount = counselorId
        ? (assignmentTab === 'leads' ? filteredLeads.length : filteredStudents.length)
        : filteredStudents.length;
    const badgeLabel = counselorId
        ? (assignmentTab === 'leads'
            ? `Lead${badgeCount !== 1 ? 's' : ''}`
            : `Student${badgeCount !== 1 ? 's' : ''}`)
        : `Student${badgeCount !== 1 ? 's' : ''}`;

    const FilterDropdown = ({ column, label }) => {
        const triggerRef = useRef(null);
        const isActive = activeFilter === column;
        const hasFilters = columnFilters[column].length > 0;
        const filteredOptions = getFilteredOptions(column);
        const allOptions = getUniqueValues(column);

        const handleToggle = () => {
            if (isActive) { setActiveFilter(null); return; }
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdownHeight = 380;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            let top = (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove)
                ? rect.bottom + 8 : rect.top - dropdownHeight - 8;
            let left = rect.left;
            const dropdownWidth = 280;
            if (left + dropdownWidth > window.innerWidth) left = window.innerWidth - dropdownWidth - 20;
            setDropdownPosition({ top, left });
            setActiveFilter(column);
        };

        return (
            <div className="filter-dropdown-container">
                <button ref={triggerRef} className={`filter-trigger ${hasFilters ? 'has-filters' : ''}`} onClick={handleToggle}>
                    {label}
                    <FontAwesomeIcon icon={faChevronDown} className="filter-icon" />
                    {hasFilters && <span className="filter-count">{columnFilters[column].length}</span>}
                </button>
                {isActive && (
                    <div className="filter-dropdown" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}>
                        <div className="filter-dropdown-header">
                            <input
                                type="text"
                                className="filter-search-input"
                                placeholder="Search..."
                                value={filterSearch[column]}
                                onChange={(e) => setFilterSearch(prev => ({ ...prev, [column]: e.target.value }))}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                        <div className="filter-options">
                            <label className="filter-option select-all">
                                <input
                                    type="checkbox"
                                    checked={columnFilters[column].length === allOptions.length && allOptions.length > 0}
                                    onChange={(e) => {
                                        if (e.target.checked) setColumnFilters(prev => ({ ...prev, [column]: allOptions }));
                                        else clearColumnFilter(column);
                                    }}
                                />
                                <span className="filter-label">Select All</span>
                            </label>
                            {filteredOptions.length > 0 ? filteredOptions.map((value, i) => (
                                <label key={i} className="filter-option">
                                    <input type="checkbox" checked={columnFilters[column].includes(value)} onChange={() => toggleFilterValue(column, value)} />
                                    <span className="filter-label">{value}</span>
                                </label>
                            )) : <div className="no-results">No results found</div>}
                        </div>
                        <div className="filter-dropdown-footer">
                            <button className="btn-clear-filter" onClick={() => clearColumnFilter(column)}>Clear</button>
                            <button className="btn-apply-filter" onClick={() => setActiveFilter(null)}>Apply</button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const printColumns = [
        { header: 'Registered ID', value: (r) => r.student_id },
        { header: 'Full Name', value: (r) => getFullName(r) },
        { header: 'Contact No', value: (r) => r.mobile || '-' },
        { header: 'Email', value: (r) => r.email },
        { header: 'Country', value: (r) => r.country || '-' },
        {
            header: 'Registered Date',
            value: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'),
        },
    ];

    return (
        <>
            <Sidebar />
            <div className="main-content" id="mainContent">
                <Header />
                <div className="students-container">

                    {/* Back banner when filtered by counselor */}
                    {counselorId && counselorInfo && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            background: '#FFF7ED', border: '1px solid #FED7AA',
                            borderRadius: '12px', padding: '14px 18px', marginBottom: '20px'
                        }}>
                            <button
                                onClick={() => navigate('/counselors')}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    background: '#F47B20', color: '#fff', border: 'none',
                                    padding: '7px 14px', borderRadius: '8px', fontSize: '13px',
                                    fontWeight: '600', cursor: 'pointer'
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                                Back
                            </button>
                            <FontAwesomeIcon icon={faUserTie} style={{ color: '#F47B20', fontSize: '18px' }} />
                            <div>
                                <div style={{ fontWeight: '700', color: '#111827', fontSize: '15px' }}>
                                    Assigned to: {counselorInfo.name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {counselorInfo.counselor_id} • {counselorInfo.email}
                                </div>
                            </div>
                            <div style={{
                                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                                <div style={{
                                    background: '#fff', color: '#1565C0', border: '1px solid #90CAF9',
                                    borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '700'
                                }}>
                                    {students.length} Student{students.length !== 1 ? 's' : ''}
                                </div>
                                <div style={{
                                    background: '#fff', color: '#6A1B9A', border: '1px solid #CE93D8',
                                    borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '700'
                                }}>
                                    {leads.length} Lead{leads.length !== 1 ? 's' : ''}
                                </div>
                                <div style={{
                                    background: '#F47B20', color: '#fff',
                                    borderRadius: '20px', padding: '4px 14px', fontSize: '13px', fontWeight: '700'
                                }}>
                                    {badgeCount} {badgeLabel}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="students-header">
                        <div className="header-left">
                            <h1>
                                <FontAwesomeIcon icon={faUsers} className="page-icon" />
                                {counselorId
                                    ? (assignmentTab === 'leads' ? 'Assigned Leads' : 'Assigned Students')
                                    : 'Students Management'}
                            </h1>
                        </div>
                        <div className="header-right">
                            <PrintReportButton
                                show={hasActiveFilters}
                                title="Students Management"
                                columns={printColumns}
                                rows={filteredStudents}
                            />
                            {hasActiveFilters && (
                                <button className="btn-clear-all-filters" onClick={clearAllFilters}>
                                    <FontAwesomeIcon icon={faTimes} /> Clear All Filters
                                </button>
                            )}
                            {!counselorId && (
                                <Link to="/create-student" className="btn-primary">
                                    <FontAwesomeIcon icon={faPlus} /> Add New Student
                                </Link>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '16px 20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fecaca' }}>
                            {error}
                        </div>
                    )}

                    {counselorId && (
                        <div style={{
                            display: 'flex', gap: '8px', marginBottom: '16px',
                            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '6px',
                            width: 'fit-content'
                        }}>
                            <button
                                type="button"
                                onClick={() => setAssignmentTab('students')}
                                style={{
                                    border: 'none', cursor: 'pointer', borderRadius: '8px',
                                    padding: '8px 18px', fontSize: '13px', fontWeight: 700,
                                    background: assignmentTab === 'students' ? '#F47B20' : 'transparent',
                                    color: assignmentTab === 'students' ? '#fff' : '#6b7280'
                                }}
                            >
                                Students ({students.length})
                            </button>
                            <button
                                type="button"
                                onClick={() => setAssignmentTab('leads')}
                                style={{
                                    border: 'none', cursor: 'pointer', borderRadius: '8px',
                                    padding: '8px 18px', fontSize: '13px', fontWeight: 700,
                                    background: assignmentTab === 'leads' ? '#F47B20' : 'transparent',
                                    color: assignmentTab === 'leads' ? '#fff' : '#6b7280'
                                }}
                            >
                                Leads ({leads.length})
                            </button>
                        </div>
                    )}

                    <div className="page-toolbar">
                        <div className="page-search">
                            <FontAwesomeIcon icon={faSearch} className="page-search-icon" />
                            <input
                                type="text"
                                className="page-search-input"
                                placeholder={
                                    counselorId && assignmentTab === 'leads'
                                        ? 'Search leads by name, email, phone or ID...'
                                        : 'Search students by name, email, mobile or ID...'
                                }
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button type="button" className="page-search-clear" onClick={() => setSearchTerm('')}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                        <DateRangeFilter
                            label={counselorId && assignmentTab === 'leads' ? 'Assigned / Created Date' : 'Registered Date'}
                            value={dateRange}
                            onChange={setDateRange}
                        />
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}><p>Loading...</p></div>
                    ) : counselorId && assignmentTab === 'leads' ? (
                        <>
                            <div className="students-table-container">
                                <table className="students-table">
                                    <thead>
                                        <tr>
                                            <th>Lead ID</th>
                                            <th>Full Name</th>
                                            <th>Contact No</th>
                                            <th>Email</th>
                                            <th>Countries</th>
                                            <th>Interest / Program</th>
                                            <th>Status</th>
                                            <th>Assigned Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedLeads.length > 0 ? (
                                            paginatedLeads.map((lead) => (
                                                <tr key={lead.id}>
                                                    <td><span className="student-id">LEAD{String(lead.id).padStart(5, '0')}</span></td>
                                                    <td>
                                                        <Link to={`/edit-lead/${lead.id}`} className="table-name-link">
                                                            {lead.full_name || '-'}
                                                        </Link>
                                                    </td>
                                                    <td>{lead.phone || '-'}</td>
                                                    <td>{lead.email || '-'}</td>
                                                    <td>{formatLeadCountries(lead)}</td>
                                                    <td>{[lead.interest, lead.program].filter(Boolean).join(' / ') || '-'}</td>
                                                    <td>
                                                        <span style={{
                                                            padding: '2px 10px', borderRadius: '20px',
                                                            fontSize: '11px', fontWeight: '600',
                                                            background: '#fef9c3', color: '#854d0e'
                                                        }}>
                                                            {lead.status || 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {(lead.assigned_at || lead.created_at)
                                                            ? new Date(lead.assigned_at || lead.created_at).toLocaleDateString()
                                                            : '-'}
                                                    </td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <Link to={`/edit-lead/${lead.id}`} className="btn-icon btn-edit" title="Edit Lead">
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </Link>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                                                    {hasActiveFilters
                                                        ? 'No leads match the selected filters'
                                                        : 'No unregistered leads assigned to this counselor yet'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <ListPagination
                                total={filteredLeads.length}
                                currentPage={leadCurrentPage}
                                totalPages={leadTotalPages}
                                onPageChange={goToLeadPage}
                                itemLabel="lead"
                            />
                        </>
                    ) : (
                        <>
                            <div className="students-table-container">
                                <table className="students-table">
                                    <thead>
                                        <tr>
                                            <th>Registered ID</th>
                                            <th><FilterDropdown column="fullName" label="Full Name" /></th>
                                            <th>Contact No</th>
                                            <th>Email</th>
                                            <th><FilterDropdown column="country" label="Country" /></th>
                                            <th>Registered Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedStudents.length > 0 ? (
                                            paginatedStudents.map((student) => (
                                                    <tr key={student.id}>
                                                        <td><span className="student-id">{student.student_id}</span></td>
                                                        <td>
                                                            <Link to={`/edit-student/${student.id}`} className="table-name-link">
                                                                {getFullName(student)}
                                                            </Link>
                                                        </td>
                                                        <td>{student.mobile || '-'}</td>
                                                        <td>{student.email}</td>
                                                        <td>{student.country || '-'}</td>
                                                        <td>
                                                            {student.created_at
                                                                ? new Date(student.created_at).toLocaleDateString()
                                                                : '-'}
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                <button
                                                                    type="button"
                                                                    className="btn-icon btn-view"
                                                                    title="View Student"
                                                                    onClick={() => setViewRecordId(student.id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faEye} />
                                                                </button>
                                                                <button
                                                                    className="btn-icon btn-credentials"
                                                                    title="View Login Credentials"
                                                                    onClick={() => handleShowCredentials(student.id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faKey} />
                                                                </button>
                                                                <Link to={`/edit-student/${student.id}`} className="btn-icon btn-edit" title="Edit Student">
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </Link>
                                                                <button className="btn-icon btn-delete" title="Delete Student" onClick={() => handleDelete(student.id, student.name)}>
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                                                    {hasActiveFilters
                                                        ? 'No students match the selected filters'
                                                        : counselorId
                                                            ? 'No students assigned to this counselor yet'
                                                            : 'No students found'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <ListPagination
                                total={filteredStudents.length}
                                currentPage={safeCurrentPage}
                                totalPages={totalPages}
                                onPageChange={goToPage}
                                itemLabel="student"
                            />
                        </>
                    )}
                </div>
            </div>

            {showCredentialsModal && (
                <>
                    <div className="modal-backdrop show" onClick={() => !credentialsLoading && setShowCredentialsModal(false)}></div>
                    <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                                    <h5 className="modal-title">
                                        <FontAwesomeIcon icon={faKey} style={{ marginRight: '10px', color: '#F47B20' }} />
                                        Student Login Credentials
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close-modal"
                                        onClick={() => setShowCredentialsModal(false)}
                                        disabled={credentialsLoading}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    {credentialsLoading ? (
                                        <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>Loading credentials…</div>
                                    ) : credentialsError ? (
                                        <div style={{
                                            background: '#fef2f2', color: '#991b1b', padding: '16px',
                                            borderRadius: '8px', border: '1px solid #fecaca', fontSize: '14px'
                                        }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
                                            {credentialsError}
                                        </div>
                                    ) : credentialsData && (
                                        <>
                                            <p style={{ fontSize: '15px', marginBottom: '16px' }}>
                                                Login details for <strong>{credentialsData.studentName}</strong>
                                                {credentialsData.studentRefId && (
                                                    <span style={{ color: '#6b7280' }}> ({credentialsData.studentRefId})</span>
                                                )}
                                            </p>
                                            <div style={{ background: '#fef3c7', padding: '20px', borderRadius: '8px', border: '2px solid #fbbf24' }}>
                                                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#92400e', fontSize: '14px' }}>
                                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
                                                    Share these credentials securely with the student
                                                </p>
                                                <div style={{ background: 'white', padding: '15px', borderRadius: '6px' }}>
                                                    <div style={{ marginBottom: '15px' }}>
                                                        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '5px' }}>Email:</label>
                                                        <div style={{ fontFamily: 'monospace', fontSize: '14px', padding: '8px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                                                            {credentialsData.email}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '5px' }}>Password:</label>
                                                        <div style={{ fontFamily: 'monospace', fontSize: '14px', padding: '8px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', fontWeight: 'bold', color: '#F47B20' }}>
                                                            {credentialsData.password}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={() => { setShowCredentialsModal(false); setCredentialsData(null); setCredentialsError(''); }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {viewRecordId && (
                <ViewRecordModal title="View Student" onClose={() => setViewRecordId(null)}>
                    <EditStudent recordId={viewRecordId} viewOnly />
                </ViewRecordModal>
            )}
        </>
    );
}